import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { normaliseText } from '@boud/contracts';
import { Icon } from '../lib/icons';

/**
 * The three fields this picker actually reads.
 *
 * Typed as its own shape rather than as `Employee`, because the meeting invitee
 * list is fed by `/employees/lookup` — an id and a name, and nothing else, so
 * that every role can invite somebody without being handed the whole HR
 * directory. Demanding the full record here would have forced that endpoint to
 * return phone numbers it has no business returning.
 */
export interface PickablePerson {
  id: string;
  name: string;
  /** Absent on the invitee lookup; present on the directory-backed pickers. */
  employer_name?: string | null;
}

export interface PeoplePickerProps {
  id: string;
  label: string;
  placeholder: string;
  people: PickablePerson[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  /** Rendered under the label, as the prototype's `.opt` marker. */
  optionalNote?: string;
  error?: string;
}

/**
 * The people picker, extracted once.
 *
 * The prototype had three near-identical copies of this — meeting invitees, room
 * allow-lists, and the visitor host — each with its own keyboard handling and
 * its own bugs. One component means one place where "Enter selects the
 * highlighted option" is true.
 *
 * It also closes the accessibility gaps all three copies shared. The prototype's
 * menu was a bare `<div>` with `role="listbox"` and no `aria-activedescendant`,
 * so a screen reader announced neither that a menu had opened nor which option
 * was highlighted. Everything added here is invisible to a sighted mouse user.
 */
export function PeoplePicker({
  id,
  label,
  placeholder,
  people,
  selectedIds,
  onChange,
  optionalNote,
  error,
}: PeoplePickerProps): ReactElement {
  const { t } = useTranslation();

  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const boxRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(
    () =>
      selectedIds
        .map((sid) => people.find((p) => p.id === sid))
        .filter(Boolean) as PickablePerson[],
    [selectedIds, people],
  );

  const available = useMemo(
    () => people.filter((p) => !selectedIds.includes(p.id)),
    [people, selectedIds],
  );

  const matches = useMemo(() => {
    // Arabic-aware, and the same normalisation the server searches with — so a
    // search for "احمد" finds "أحمد" here exactly as it does there (BR-042).
    const needle = normaliseText(query);
    if (!needle) return available;
    return available.filter(
      (p) =>
        normaliseText(p.name).includes(needle) ||
        normaliseText(p.employer_name ?? '').includes(needle),
    );
  }, [available, query]);

  /**
   * Closes on a click anywhere outside.
   *
   * On `mousedown` rather than `click`, and paired with `preventDefault` on the
   * options themselves, so that selecting an option does not first blur the
   * input and cancel the selection — the bug all three prototype copies worked
   * around individually.
   */
  useEffect(() => {
    if (!isOpen) return;

    function onDocumentMouseDown(event: MouseEvent): void {
      if (!boxRef.current?.contains(event.target as Node)) setIsOpen(false);
    }

    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, [isOpen]);

  function add(personId: string): void {
    if (!selectedIds.includes(personId)) onChange([...selectedIds, personId]);
    setQuery('');
    setActiveIndex(-1);
  }

  function remove(personId: string): void {
    onChange(selectedIds.filter((sid) => sid !== personId));
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex((index) => Math.min(index + 1, matches.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      const person = matches[activeIndex];
      if (person) {
        event.preventDefault();
        add(person.id);
      }
    } else if (event.key === 'Escape') {
      // Stopped here so it closes the menu without also closing the dialog the
      // picker sits in. One Escape, one thing.
      if (isOpen) {
        event.stopPropagation();
        setIsOpen(false);
        setActiveIndex(-1);
      }
    }
  }

  const listId = `${id}-menu`;
  const activeId =
    activeIndex >= 0 && matches[activeIndex] ? `${id}-opt-${activeIndex}` : undefined;

  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        {optionalNote ? <span className="opt"> {optionalNote}</span> : null}
      </label>

      <div className="peoplebox" ref={boxRef}>
        <div className="search" style={{ background: 'var(--surface)' }}>
          <Icon name="search" />
          <input
            id={id}
            autoComplete="off"
            placeholder={placeholder}
            value={query}
            role="combobox"
            aria-expanded={isOpen}
            aria-controls={listId}
            aria-autocomplete="list"
            {...(activeId ? { 'aria-activedescendant': activeId } : {})}
            aria-invalid={Boolean(error)}
            onFocus={() => setIsOpen(true)}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(true);
              setActiveIndex(-1);
            }}
            onKeyDown={onKeyDown}
          />
        </div>

        <div className={isOpen ? 'people-menu open' : 'people-menu'} id={listId} role="listbox">
          {available.length === 0 ? (
            <div className="people-empty">{t('bk_all_added')}</div>
          ) : matches.length === 0 ? (
            <div className="people-empty">{t('bk_no_person')}</div>
          ) : (
            matches.map((person, index) => (
              <div
                key={person.id}
                id={`${id}-opt-${index}`}
                role="option"
                aria-selected={index === activeIndex}
                className={index === activeIndex ? 'people-opt active' : 'people-opt'}
                // `mousedown`, not `click` — see the effect above.
                onMouseDown={(event) => {
                  event.preventDefault();
                  add(person.id);
                }}
                onMouseEnter={() => setActiveIndex(index)}
              >
                <span className="pa">{initialsOf(person.name)}</span>
                <span>
                  <span className="pn">{person.name}</span>
                  <span className="pco">{person.employer_name}</span>
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="attendees">
        {selected.map((person) => (
          <span className="attendee-chip" key={person.id}>
            <Icon name="users" className="ac-ic" />
            {person.name}
            <button
              type="button"
              className="rm"
              onClick={() => remove(person.id)}
              aria-label={`${t('remove')} ${person.name}`}
            >
              <Icon name="x" />
            </button>
          </span>
        ))}
      </div>

      {/* Announced politely: a count changing is information, not an alarm. */}
      <div className="people-hint" aria-live="polite">
        {available.length === 0
          ? t('bk_all_added')
          : selected.length > 0
            ? t('people_selected_count', { selected: selected.length, available: available.length })
            : ''}
      </div>

      {error ? (
        <div className="field-error" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .map((word) => word[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}
